import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button } from '@gouvfr-lasuite/cunningham-react';
import { HorizontalSeparator, ShareModal } from '@gouvfr-lasuite/ui-kit';

import { useUsersSearch } from '../../../hooks';
import usePopup from '../../../lib/popup/use-popup';
import InformationEdit from './InformationEdit';
import DeleteStep from '../../../steps/DeleteStep';

import styles from './GeneralPane.module.scss';

const GeneralPane = React.memo(
  ({
    name,
    roleAssignments,
    projectManagerRole,
    currentProjectId,
    searchedUsers,
    isSearchingUsers,
    onUpdate,
    onDelete,
    onRoleAssignmentCreate,
    onRoleAssignmentDelete,
    onSearchUsers,
    onClearUserSearch,
  }) => {
    const [t] = useTranslation();

    const [isShareModalOpen, setIsShareModalOpen] = useState(false);

    const handleShareClick = useCallback(() => {
      setIsShareModalOpen(true);
    }, []);

    const handleShareModalClose = useCallback(() => {
      setIsShareModalOpen(false);

      onClearUserSearch(); // Empty the search for a proper next modal opening
    }, [onClearUserSearch]);

    // const userIdsToExclude = useMemo(() => managers.map((manager) => manager.user.id), [managers]);

    const userIdsToExclude = useMemo(
      () => roleAssignments.map((assignment) => assignment.userId),
      [roleAssignments],
    );

    const [debouncedHandleUsersQuery] = useUsersSearch(userIdsToExclude, onSearchUsers);

    const formattedSearchedUsers = useMemo(() => {
      return searchedUsers.map((user) => {
        return {
          id: user.id,
          full_name: user.name,
          username: user.username,
          email: user.email,
        };
      });
    }, [searchedUsers]);

    // const modalMembers = useMemo(() => {
    //   return managers.map((manager) => {
    //     return {
    //       id: manager.id,
    //       role: 'manager',
    //       user: {
    //         id: manager.user.id,
    //         full_name: manager.user.name,
    //         email: manager.user.email,
    //       },
    //     };
    //   });
    // }, [managers]);

    const modalMembers = useMemo(() => {
      return roleAssignments.map((assignment) => {
        // console.log('assignment');
        // console.log(JSON.stringify(assignment, null, 2));

        return {
          id: assignment.id,
          role: assignment.role.code,
          user: {
            id: assignment.user.id,
            full_name: assignment.user.name,
            email: assignment.user.email,
          },
        };
      });
    }, [roleAssignments]);

    // console.log('blablablabalbalbalba');
    // console.log(modalMembers);

    const DeletePopup = usePopup(DeleteStep);

    return (
      <div className={styles.pane}>
        <InformationEdit
          defaultData={{
            name,
          }}
          onUpdate={onUpdate}
        />
        <HorizontalSeparator />
        <div className={styles.permissionsSection}>
          <h4>
            {t('common.permissions', {
              context: 'title',
            })}
          </h4>
          <div>
            <Button onClick={handleShareClick}>{t('common.editProjectManagers')}</Button>
          </div>
        </div>
        <HorizontalSeparator />
        <div className={styles.dangerSection}>
          <h4>
            {t('common.dangerZone', {
              context: 'title',
            })}
          </h4>
          <div>
            <DeletePopup
              title="common.deleteProject"
              content="common.areYouSureYouWantToDeleteThisProject"
              buttonContent="action.delete"
              onConfirm={onDelete}
            >
              <Button color="error" variant="primary">
                {t('action.deleteProject', {
                  context: 'title',
                })}
              </Button>
            </DeletePopup>
          </div>
        </div>

        <ShareModal
          key={isShareModalOpen ? 'open' : 'closed'} // [WORKAROUND] To avoid previous search input to appear again after opening the modal
          // TODO: should be adjusted to avoid letting inviting random email since it has no reality then (ref: https://github.com/suitenumerique/ui-kit/issues/151)
          // for now we are using `patch-package` to fix this
          isOpen={isShareModalOpen}
          onClose={handleShareModalClose}
          modalTitle={t('common.managers_title')} // For whatever reason `{ context: 'title' }` is not working here, so using directly the suffix (missing context since modal?)
          canView
          canUpdate
          accesses={modalMembers}
          invitationRoles={[
            {
              label: t('common.manager'),
              value: 'project_manager',
            },
          ]}
          onDeleteAccess={(e) => {
            onRoleAssignmentDelete(e.id);
          }}
          onDeleteInvitation={() => {}}
          onUpdateInvitation={() => {}}
          onUpdateAccess={() => {
            // No need since only 1 role
          }}
          onSearchUsers={debouncedHandleUsersQuery}
          loading={isSearchingUsers}
          hasNextMembers={false}
          hasNextInvitations={false}
          searchUsersResult={formattedSearchedUsers}
          onInviteUser={(users) => {
            users.forEach((user) => {
              onRoleAssignmentCreate({
                userId: user.id,
                resourceType: 'Project',
                resourceId: currentProjectId,
                roleId: projectManagerRole.id,
              });
            });
          }}
        />
      </div>
    );
  },
);

GeneralPane.propTypes = {
  name: PropTypes.string.isRequired,
  roleAssignments: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  projectManagerRole: PropTypes.shape({
    id: PropTypes.number,
    code: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
  currentProjectId: PropTypes.number.isRequired,
  searchedUsers: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  isSearchingUsers: PropTypes.bool.isRequired, // eslint-disable-line react/forbid-prop-types
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onRoleAssignmentCreate: PropTypes.func.isRequired,
  onRoleAssignmentDelete: PropTypes.func.isRequired,
  onSearchUsers: PropTypes.func.isRequired,
  onClearUserSearch: PropTypes.func.isRequired,
};

export default GeneralPane;
